import smart_links from './smart_links'

export default {
  smart_links,
  ordering_not_enabled_page: {
    title: 'Your Thanx account is not integrated with online ordering',
    description:
      'Supercharge your customer database with Thanx-powered digital ordering integrations. Thanx offers a variety of ways to integrate, whether it’s working with your current setup or creating a seamless transition to our native ordering experience. We offer robust app and web configurations to best suit your needs.',
    contact_our_team: 'Contact our team to learn about Thanx Ordering',
    image_alt:
      'Example of Thanx Ordering for web (right) and native app (left)',
  },
  ordering_configuration: {
    title: 'Ordering configuration',
    description:
      'A large amount of information comes directly from your ordering provider which Thanx then displays. Information from your ordering provider includes:',
    image_alt: 'Ordering config',
    contact_your_provider:
      'Contact your ordering provider to update your ordering configuration. Changes with your provider may take a few moments to be reflected in your ordering experience.',
    info: 'Info',
    list: {
      menu_item: 'Menu item images',
      category_order: 'Category order',
      ordering_categories: 'Ordering categories, items, modifiers',
      pricing: 'Pricing',
      handoff: 'Handoff mode configuration (delivery, pickup, etc.)',
      location_availability: 'Location availability',
      location_hours: 'Location hours',
    },
  },
  ordering_error_messages: {
    title: 'Ordering error messages',
  },
  receipt_settings: {
    title: 'Receipt settings',
    description:
      'Customers can upload receipts in your app to earn reward progress. You may customize which type of receipts are accepted. See your current configuration below.',
    receipt_type: 'Receipt type',
    accepted: 'Accepted?',
    cash: 'Cash receipts',
    gift_card: 'Gift card receipts',
    online: 'Online receipts',
    third_party: '3rd party delivery receipts',
    yes: 'Yes',
    no: 'No',
    store_credit: 'Store credit',
    publish: 'Publish',
    form_error:
      'One or more fields are missing or incorrect. Please check the page for errors.',
    recommended: 'Recommended',
    fetch_error: 'Failed to load receipt settings. Please try refreshing.',
    submit_error: 'Something went wrong. Please try again.',
    submit_success: 'Your changes to your receipt settings are now live.',
    proof_of_payment: {
      title: 'Proof of payment required',
      description:
        'Configure whether receipts must show proof of payment (a payment method visible on the receipt) to be eligible for reward progress.',
      required: 'Only issue points for receipts with proof of payment visible',
      required_explanation:
        'Receipts without a visible payment method will be rejected. This helps ensure accurate tracking of eligible purchases.',
      optional:
        "Also issue points for receipts that don't have proof of payment",
      optional_explanation:
        'Receipts without a visible payment method will still be accepted and earn reward progress.',
    },
    modal: {
      publish_changes: 'Publish changes',
      you_are_publishing:
        'You are publishing changes to <strong>Receipt settings</strong><br/>Publishing these changes will push them live to your app.',
      publish: 'Yes, publish changes',
    },
  },
  instore_purchase_tracking: {
    title: 'In-store purchase tracking',
    description: 'How in-store purchase data is acquired',
    learn_more: 'Learn more',
    contact_link: 'Contact us to switch',
    card_enrollment: {
      title: 'Card enrollment',
      description:
        'Automatic when customers make purchases with an enrolled credit card',
    },
    manual_check_in: {
      title: 'Manual check-in',
      description:
        'Guests check in at the POS (via phone, email, or QR code) each time they make a purchase',
    },
  },
  reward_expiration: {
    title: 'Rewards settings',
    tab_title: 'Settings',
    description: '',
    section_title: 'Expiration for granted rewards',
    section_description:
      'Choose how long customers have to use the rewards you grant them for support purposes. This does not apply to campaigns, only customer-support rewards granted one at a time to consumers (also applies to RecoveryAI granted rewards).',
    publish: 'Publish',
    publish_requires_manage:
      'You need the Manage Merchant Settings permission to publish changes.',
    publish_requires_manage_aria:
      'Publish disabled — Manage Merchant Settings permission required',
    submit_success: 'Your setting is being applied.',
    submit_error: 'Something went wrong. Please try again.',
    options: {
      no_expiration: {
        title: 'No expiration',
        description: '',
      },
      expires_after: {
        title: 'Expires after',
        suffix: 'days',
        input_aria_label: 'Number of days until expiration',
        error: 'Enter a number of days between 1 and 999.',
        description: '',
      },
    },
    impact_preview: {
      loading: 'Calculating impact...',
      error: 'Couldn’t load the impact preview. Please try again.',
    },
    rich_callout: {
      title:
        'Apply this expiration to already granted rewards without expiration dates',
      body: 'This will update <strong>~%{rewardsCount}</strong> already granted rewards that do not already have expiration dates. Rewards that already have expiration dates won’t be changed.',
    },
    confirm_modal: {
      title: 'Confirm changes to already granted rewards',
      body: 'You’re about to apply this expiration to <strong>~%{rewardsCount}</strong> already granted rewards that do not currently have expiration dates. These rewards will expire <strong>%{days} days</strong> from today.',
      warning:
        'Expiration dates added to already granted rewards can’t be undone.',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
  },
  instore_enrollment: {
    title: 'In-store enrollment',
    description:
      'Configure when guests are prompted to sign up at the POS during in-store visits.',
    learn_more: 'Learn more about in-store enrollment at the POS',
    learn_more_url:
      'https://help.thanx.com/hc/en-us/articles/36605477074327-Enrollment-at-the-POS',
    section_title: 'In-store',
    publish: 'Publish',
    submit_success:
      'Your changes to in-store enrollment settings are now live.',
    submit_error: 'Something went wrong. Please try again.',
    opens_in_new_tab: 'opens in a new tab',
    options: {
      never: {
        title: 'Never prompt guests to signup at the POS',
        description:
          'Guests will not get enrollment prompts from Thanx at the POS',
      },
      on_request: {
        title: 'Only prompt when guests ask to enroll*',
        description:
          'Thanx will only issue a prompt when a guest (or staff member on their behalf) explicitly chooses to create a new account.',
        description_link:
          'Requires all POS systems to support in-store enrollment.',
        toast_note: {
          label: 'For Toast:',
          description:
            'Contact your Thanx and Toast reps to enable in-store enrollment.',
          link: 'Learn more.',
          link_url:
            'https://help.thanx.com/hc/en-us/articles/36605477074327-In-store-Enrollment-at-the-POS#h_toast_gfd_signup',
        },
      },
      always: {
        title:
          'Prompt when guests ask to enroll or when they enter an unknown phone/email*',
        description:
          "Thanx will automatically prompt guests either when they ask to enroll or when they enter a phone number/email that's not found in your CRM.",
        description_link: 'Works with all integrated POS systems.',
        description_suffix:
          'Guests will receive no more than 1 prompt per day.',
      },
    },
    cost_note: 'Cost: $0.0118 per SMS (pass-through)',
    modal: {
      publish_changes: 'Publish changes',
      you_are_publishing:
        'You are publishing changes to <strong>In-store enrollment</strong><br/>Publishing these changes will push them live to your POS.',
      publish: 'Yes, publish changes',
    },
  },
  terms_and_policies: {
    title: 'Terms and policies',
    description:
      'Manage the terms and policy documents that customers agree to when creating an account.',
    sidebar: {
      modal: {
        publish_changes: 'Publish changes',
        you_are_publishing:
          'You are publishing changes to <strong>Terms and policies</strong><br/>Publishing these changes will push them live to your app.',
        publish: 'Yes, publish changes',
      },
    },
    nav: {
      title: 'Configuration',
      sub_title: 'Terms and Policies Builder',
      save_button: 'Publish',
      saving: 'Saving ...',
      cancel: 'Cancel',
    },
    modal: {
      title: 'Publish this document',
      body: `Any changes you've made will be updated immediately.`,
      buttons: {
        publish: 'Publish',
        cancel: 'Cancel',
      },
    },
    tabs: {
      enter_custom_text: 'Enter Custom Text',
      provide_a_url: 'Provide a URL',
      preview: 'Preview',
      enter_custom_text_title: 'COPY/PASTE A FILE',
      provide_a_url_title: 'LINK TO URL',
    },
    custom_text: 'You are using custom text',
    url_custom_text: 'You are using a URL',
    last_updated_text: 'Last updated',
    url_text: 'URL',
    url_label: 'Link a hosted document',
    url_subtitle:
      'Use this option if you want to host your own legal document.',
    url_placeholder: 'Enter a URL',
    url_validation_error: `Please enter a valid URL like (https://www.example.com/)`,
    privacy_policy: {
      title: 'Privacy policy',
      default_subtitle: 'You are using default privacy policy text',
      download_text: 'Download a sample privacy policy document',
      view_btn_text: 'View',
      edit_default_btn_text: 'Manage your custom privacy policy',
      edit_btn_text: 'Edit',
      sample_file_download_error:
        'Failed to download sample privacy policy document',
    },
    terms_of_service: {
      title: 'Terms of service',
      default_subtitle: 'You are using default terms of service text',
      download_text: 'Download a sample terms of service document',
      view_btn_text: 'View',
      edit_default_btn_text: 'Manage your custom terms of service',
      edit_btn_text: 'Edit',
      sample_file_download_error:
        'Failed to download sample terms of service document',
    },
    ccpa: {
      title: 'Tracking Opt-Out Guide (CCPA compliant)',
      default_subtitle: 'You are using the default tracking opt-out guide text',
      download_text: 'Download a sample opt-out document',
      view_btn_text: 'View',
      edit_default_btn_text: 'Manage your custom opt-out guide',
      edit_btn_text: 'Edit',
      sample_file_download_error:
        'Failed to download sample Tracking Opt-Out Guide document',
    },
    suppression_email: {
      title: 'Suppression notification email',
      description:
        'Designate an email address to receive CCPA opt-out notifications and suppression digests.',
      empty_description:
        'No email address configured. Add an email to receive suppression notifications.',
      hint: 'We recommend using a shared or team email (e.g. legal@yourbrand.com) to protect against employee turnover.',
      help_link_text: 'Learn more about email suppression',
      help_link_url:
        'https://help.thanx.com/hc/en-us/articles/38946440916119-Email-Suppression-List',
      manage_button: 'Manage email',
      add_button: 'Add email',
      save_button: 'Save',
      cancel_button: 'Cancel',
      placeholder: 'e.g. legal@yourbrand.com',
      validation_error: 'Please enter a valid email address',
      single_email_validation_error: 'Please only enter a single email address',
      save_success: 'Suppression notification email saved.',
      save_error: 'Failed to save email. Please try again.',
      clear_success: 'Suppression notification email removed.',
    },
    recovery_ai_terms: {
      title: 'RecoveryAI Terms of Service',
      description:
        'The terms that apply to a merchant using the RecoveryAI functionality.',
      view_button: 'View',
    },
    errors: {
      load_error:
        'There was an error loading your terms and policies. Please try again.',
      documentbuilder: {
        image_upload_error:
          'Something went wrong uploading your image. Please try again.',
        load_error:
          'There was an error loading the document builder. Please ensure any ad blockers are disabled and your browser is configured to allow third-party cookies.',
        preview_error:
          'There was an error loading the preview. Please try again.',
        publish_error:
          'There was an error publishing your changes. Please try again.',
        save_error: 'There was an error saving your changes. Please try again.',
        create_error:
          'There was an error creating your document. Please try again.',
      },
    },
  },
  post_order_feedback: {
    title: 'Post order receipt emails',
    description:
      'Control the post-order receipt email customers receive after they place an order.',
    publish: 'Publish',
    email_channel: {
      title: 'Email',
      // Fallback for merchants without a whole-email provider (e.g. no
      // ordering provider): the toggle only removes the thumbs-up/thumbs-down
      // prompt from the email.
      thumbs_prompt: {
        label: 'Send post-order feedback prompts via email',
        description:
          'When enabled, customers will receive a thumbs-up/thumbs-down feedback prompt in the post-order email after they place an order.',
      },
      // Olo/Deliverect: the toggle suppresses the whole post-order email
      // (see nexus Orders::SendConfirmationEmail#olo_email_enabled? /
      // #deliverect_email_enabled?).
      whole_email: {
        label: 'Send post-order receipts to gather feedback via email',
        description:
          'When enabled, customers will receive a post-order email receipt that includes a thumbs-up/thumbs-down prompt to measure their order satisfaction. When disabled, customers will not receive a post-order email receipt at all.',
      },
    },
    alert: {
      submit_success:
        'Your changes to your post order receipt email settings are now live.',
      submit_error: 'This page could not be saved. Please try again.',
    },
  },
  scannable_codes: {
    title: 'Scannable codes',
    description:
      'Customize the information customers see in apps to identify themselves at the POS during in-store visits.',
    publish: 'Publish',
    qr_code_preview:
      'Example of the native app with a QR code shown in the bottom center of the reward details screen',
    qr_codes: {
      title: 'QR codes',
      label: 'My POS system supports scanning QR codes',
      description:
        'Customers will see QR codes on the POS integrated screens (e.g. Reward Redemption, Earn points, Gift cards).',
      learn_more: 'Available on integrated POS.',
      learn_more_url:
        'https://help.thanx.com/hc/en-us/articles/41953681100311-POS-Partner-Integrations',
    },
    alert: {
      submit_success:
        'Your changes to your Scannable codes settings are now live. Refresh your mobile app to see them.',
      submit_error: 'This page could not be saved. Please try again.',
    },
  },
  email_reply_routing: {
    title: 'Email reply addresses',
    description:
      'Choose where replies to the marketing and order emails Thanx sends on your behalf are delivered.',
    marketing_label: 'Marketing emails',
    transactional_label: 'Order & receipt emails',
    option_no_reply: 'no-reply@thanx.com',
    no_reply_note:
      'Note: email replies sent to this address are not read or monitored by Thanx.',
    option_address: 'Send replies to an email address',
    address_placeholder: 'replies@yourbrand.com',
    address_aria_label: 'Reply-to email address',
    address_required: 'Enter an email address, or select no-reply@thanx.com.',
    address_invalid: 'Enter a valid email address.',
    address_thanx_blocked: 'Enter an email address outside of thanx.com.',
    save_button: 'Save',
    save_success: 'Your email reply addresses have been saved.',
    save_error: 'These settings could not be saved. Please try again.',
    load_error: 'These settings could not be loaded. Please try again.',
  },
  analytics: {
    tab: 'Facebook and Google Analytics',
    enabled_title: 'Facebook and Google Analytics (for online ordering)',
    enabled_description:
      'Measure every step of your online ordering funnel — including conversions, basket abandonment, SKU level data and more. Use this data to attribute real ROI from online orders to your marketing campaigns on Facebook, Google, or any ad platform supported by Google Tag Manager.<br /><br />Connect the tools below to start tracking ordering conversion metrics.',
    disabled_title: 'Your Thanx account is not integrated with online ordering',
    disabled_description:
      'An online ordering integration is required to connect to Facebook and Google Analytics.<br /><br />Supercharge your customer database with Thanx-powered digital ordering integrations. Thanx offers a variety of ways to integrate, whether its working with your current setup or creating a seamless transition to our native ordering experience. We offer robust app and web configurations to best suit your needs. <br /><br />Then, measure every step of your online ordering funnel—including conversions, basket abandonment, SKU level data and more. Use this data to attribute real ROI from online orders to your Facebook and Google marketing campaigns.',
    where_to_text: 'Where to find this ID',
    example: 'Example of Thanx Ordering for web (right) and native app (left)',
    contact_disabled:
      'Contact our team to learn about Thanx ordering and analytics',
    how_to_text: 'How to set up %{title}',
    connect: 'Connect',
    update: 'Update',
    contact: {
      title: 'Need to track your app data with Facebook or Google Analytics?',
      description:
        'Contact us to learn how to connect your app to your tracking tools. You may be asked to provide additional information and permissions to our team. App update will be required.',
      button: 'Contact us',
      how_to_link:
        'https://help.thanx.com/hc/en-us/articles/17663450010135-Google-Analytics-in-Thanx-mobile-apps',
      how_to_text: 'How to set up',
    },
    processing: 'Processing %{label}',
    successful: 'Your update to %{label} has been processed successfully',
    options: {
      facebook_pixel_id: {
        title: 'Meta Pixel',
        description:
          'Track your online ordering end-to-end funnel metrics with Meta.',
        label: 'Pixel ID',
      },
      google_tag_manager_id: {
        title: 'Google Tag Manager',
        description:
          'Track marketing to online conversion across all your third-party vendors.<br />Manage tags for conversion tracking, site analytics, remarketing and more.',
        label: 'Container ID',
      },
      google_measurement_id: {
        title: 'Google Analytics',
        description:
          'Track your online ordering end-to-end funnel metrics with Google Analytics.',
        label: 'Google Measurement ID',
      },
    },
  },
  unavailable_item_visibility: {
    title: '86ed items',
    description:
      'Choose how unavailable menu or modifier items appear to customers \u2014 either hidden from view or shown as disabled with \u201cNot Available\u201d messaging.',
    fetch_error:
      'Failed to load unavailable item visibility setting. Please try refreshing.',
    submit_error: 'Something went wrong. Please try again.',
    submit_success: 'Your changes to unavailable item visibility are now live.',
    section_title: 'Settings',
    field_label: 'When menu items or modifiers are unavailable',
    edit: 'Edit',
    configuration: 'Configuration',
    exit_without_saving: 'Exit without saving',
    publish_changes: 'Publish changes',
    options: {
      disabled: 'Display as \u201cNot Available\u201d',
      disabled_explanation:
        'Keep unavailable items visible but grayed out with a \u201cNot Available\u201d message.',
      hidden: 'Hide unavailable items',
      hidden_explanation:
        'Remove unavailable items from the menu so customers only see what\u2019s in stock.',
    },
  },
  time_based_menu_visibility: {
    title: 'Time-based menu items',
    description:
      'Choose how menu items that are only orderable at certain times of day appear when they\u2019re unavailable for a customer\u2019s order \u2014 whether ordering now or scheduling ahead \u2014 either shown with their available hours or hidden from the menu.',
    fetch_error:
      'Failed to load time-based menu visibility setting. Please try refreshing.',
    submit_error: 'Something went wrong. Please try again.',
    submit_success: 'Your changes to time-based menu visibility are now live.',
    section_title: 'Settings',
    field_label: 'When menu items are unavailable for the order time',
    options_label: 'Time-based menu item visibility',
    edit: 'Edit',
    configuration: 'Configuration',
    exit_without_saving: 'Exit without saving',
    publish_changes: 'Publish changes',
    options: {
      visible: 'Show items with their available hours',
      visible_explanation:
        'Keep items on the menu when they\u2019re unavailable for the order time, labeled with the hours they can be ordered.',
      hidden: 'Hide items unavailable for the order time',
      hidden_explanation:
        'Remove items from the menu when they\u2019re unavailable for the order time, so customers only see what\u2019s actually available to order.',
    },
  },
  promo_code_visibility: {
    title: 'Promo code visibility',
    description:
      'Control whether customers can see and enter a promo code at checkout.',
    field_label: 'Hide the promo code field at checkout',
    field_description:
      'When enabled, the promo code field is hidden at checkout for every location, regardless of individual location settings.',
    disabled_permission_reason:
      'You need permission to manage ordering settings to change this.',
    alerts: {
      fetch_error:
        'Failed to load promo code visibility setting. Please try refreshing.',
      hide_success: 'The promo code field is now hidden at checkout.',
      show_success: 'The promo code field is now visible at checkout.',
      save_error: 'Something went wrong. Please try again.',
    },
  },
  delivery_default_logic: {
    title: 'Delivery default',
    description:
      'Control how delivery orders are assigned to stores. Choose whether customers must order from the closest eligible location or allow them to place delivery orders from any store.',
    publish: 'Publish',
    sidebar_preview: 'Delivery default preview',
    form_error:
      'One or more fields are missing or incorrect. Please check the page for errors.',
    recommended: 'Recommended',
    fetch_error:
      'Failed to load delivery default logic. Please try refreshing.',
    submit_error: 'Something went wrong. Please try again.',
    submit_success: 'Your changes to your Delivery Default Logic are now live.',
    web_and_app: {
      title: 'Web & App',
      allow_customers: 'Allow customers to choose any store for delivery',
      allow_customers_explanation:
        'Customers can select from any store offering delivery to their address. Choose this option if you have multiple stores in overlapping delivery zones.',
      restrict_delivery: 'Restrict delivery to the nearest store',
      restrict_delivery_explanation:
        'Customers will be automatically assigned to the closest location that delivers to them. This is the default behavior and helps optimize delivery routes.',
    },
    learn_more: 'Learn more',
    modal: {
      publish_changes: 'Publish changes',
      you_are_publishing:
        'You are publishing changes to <strong>Delivery Default Logic</strong><br/>Publishing these changes will push them live to your app and/or website.',
      publish: 'Yes, publish changes',
    },
  },
  checkout_message: {
    title: 'Custom checkout message',
  },
  preferences_modal_setting: {
    title: 'Location selection',
    description:
      'Configure if customers are required to manually select a location before starting a new order.',
    publish: 'Publish',
    form_error:
      'One or more fields are missing or incorrect. Please check the page for errors.',
    recommended: 'Recommended',
    fetch_error:
      'Failed to load order location selection. Please try refreshing.',
    submit_error: 'Something went wrong. Please try again.',
    submit_success:
      'Your changes to your Order location selection are now live.',
    app_selection: {
      title: 'App',
      never: 'Do not interrupt new orders with manual location selection',
      never_explanation:
        'New orders automatically set the location based on the previous order, or set the nearest location based on GPS or IP address. Customers can still access the location selection screen manually.',
      always: 'Interrupt new orders with manual location selection',
      always_explanation:
        'Ask customers to select a location on every new order.',
      first_order:
        'Interrupt only the first order with manual location selection',
      first_order_explanation:
        'Ask customers to select a location on their first order. For future new orders automatically select the location from the previous order.',
    },
    web_selection: {
      title: 'Web',
      never: 'Do not interrupt new orders with manual location selection',
      never_explanation:
        'New orders automatically set the location based on the previous order, or set the nearest location based on GPS or IP address. Customers can still access the location selection screen manually.',
      always: 'Interrupt new orders with manual location selection',
      always_explanation:
        'Ask customers to select a location on every new order.',
      first_order:
        'Interrupt only the first order with manual location selection',
      first_order_explanation:
        'Ask customers to select a location on their first order. For future new orders automatically select the location from the previous order.',
    },
    existing_order_tip: {
      title: 'Existing Order Tip',
      body: 'Customers that are resuming building an existing order will not be prompted to select a location each time they come back to the ordering experience.',
    },
    modal: {
      publish_changes: 'Publish changes',
      you_are_publishing:
        'You are publishing changes to <strong>Order location selection</strong><br/>Publishing these changes will push them live to your app and/or website.',
      publish: 'Yes, publish changes',
    },
  },
  revenue_centers: {
    title: 'Revenue Center',
    description:
      'Tell Thanx which %{provider} Revenue Center to send orders to.',
    tabs: {
      default: 'Default',
      handoff_modes: 'By handoff mode',
    },
    tablist_label: 'Revenue Center settings',
    sync: {
      synced: 'Synced from %{provider} %{time}',
      never: 'Not yet synced from %{provider}',
      auto: 'Auto-syncs every 5 minutes',
      sync_now: 'Sync now',
      started: 'Syncing Revenue Centers from %{provider}…',
      error: 'Failed to start sync. Please try again.',
    },
    default_tab: {
      section_title: 'Revenue Center',
      helper:
        'Thanx will send this Revenue Center on every order, across every location and handoff mode.',
      field_label: 'Revenue Center',
      footnote:
        'The selected Revenue Center is what Thanx sends to %{provider} on every order. You can change it at any time; new orders take effect immediately.',
      scope: 'Applies to all of your locations and every handoff mode.',
      empty: 'No Revenue Center selected',
    },
    archived_banner: {
      title: '%{name} was archived in %{provider}',
      body: 'The Revenue Center you selected, %{name}, was archived in %{provider}. Orders placed through %{name} have been failing since then. Pick a replacement Revenue Center to keep orders flowing.',
      cta: 'Pick a replacement Revenue Center',
    },
    handoff_tab: {
      section_title: 'Revenue Center by handoff mode',
      intro: 'Pick a Revenue Center for each ordering channel.',
      columns: {
        handoff: 'Handoff mode',
        status: 'Status',
        revenue_center: 'Revenue Center',
      },
      inherited: 'Inherited from brand default',
      status_enabled: 'Enabled',
      status_default: 'Default',
      status_not_enabled: 'Not Enabled',
      not_supported: 'Not enabled at any of your locations',
      placeholder: 'Uses default Revenue Center',
    },
    row: {
      placeholder: 'Select a Revenue Center',
      no_selection: 'No Revenue Center selected',
      archived_suffix: ' (archived)',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      clear: 'Clear',
    },
    handoffs: {
      pickup: 'Pickup',
      delivery: 'Delivery',
      curbside: 'Curbside',
      tableside: 'Tableside',
      drivethru: 'Drive-thru',
    },
    handoff_descriptions: {
      pickup: 'In-store pickup',
      delivery: 'Thanx-delivered orders',
      curbside: 'Curbside handoff',
      tableside: 'Order at the table',
      drivethru: 'Drive-thru lane',
    },
    empty_catalog:
      'No Revenue Centers have been synced from %{provider} yet. Trigger a sync to populate the list.',
    fetch_error: 'Failed to load Revenue Centers. Please try refreshing.',
    save_success: 'Your Revenue Center selection has been saved.',
    save_error: 'Failed to save. Please try again.',
    save_error_handoffs:
      "Couldn't save changes for %{names}. Please try again.",
    save_error_handoffs_partial:
      "Couldn't save changes for %{names}. Please try again. Your other changes were saved.",
    clear_success: 'Revenue Center selection cleared.',
    clear_error: 'Failed to clear. Please try again.',
  },
}
